import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, Receipt } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { toast } from '@/components/ui/Toast'
import { brl } from '@/lib/format'
import { PartnerService } from '@/services/modules/PartnerService'
import type { Revenue } from '@/services/modules/RevenuesService'

const CUSTOMER_FILTERS = [
  { label: 'Todos', value: '' },
  { label: 'Empresas', value: 'company' },
  { label: 'Associados', value: 'subscriber' },
] as const
type CustomerFilter = (typeof CUSTOMER_FILTERS)[number]['value']

const STATUS_DOT: Record<string, { color: string; label: string }> = {
  PENDENTE:    { color: 'bg-amber-400',   label: 'Pendente' },
  PAGO:        { color: 'bg-emerald-500', label: 'Pago' },
  ISENTO:      { color: 'bg-blue-500',    label: 'Isento' },
  CANCELADO:   { color: 'bg-rose-500',    label: 'Cancelado' },
  RECUSADO:    { color: 'bg-rose-500',    label: 'Recusado' },
  REEMBOLSADO: { color: 'bg-blue-500',    label: 'Reembolsado' },
  PROCESSADO:  { color: 'bg-blue-500',    label: 'Processado' },
}

const PAYMENT_METHODS = [
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CARTÃO DE CRÉDITO', label: 'Cartão de crédito' },
  { value: 'PIX', label: 'PIX' },
  { value: 'DEPÓSITO', label: 'Depósito' },
  { value: 'TRANSFERÊNCIA BANCÁRIA', label: 'Transferência' },
  { value: 'DINHEIRO', label: 'Dinheiro' },
]

const STATUS_OPTIONS = [
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'PAGO', label: 'Pago' },
  { value: 'ISENTO', label: 'Isento' },
  { value: 'CANCELADO', label: 'Cancelado' },
]

interface Filters {
  name: string
  dueFrom: string; dueTo: string
  paidFrom: string; paidTo: string
  method: string
  amountFrom: number | null; amountTo: number | null
  status: string
}
const EMPTY_FILTERS: Filters = {
  name: '', dueFrom: '', dueTo: '', paidFrom: '', paidTo: '',
  method: '', amountFrom: null, amountTo: null, status: '',
}

function RevenueStatusDot({ status }: { status: string }) {
  const cfg = STATUS_DOT[status] ?? { color: 'bg-gray-300', label: status }
  return (
    <span className="inline-flex items-center justify-center" title={cfg.label}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${cfg.color}`} />
    </span>
  )
}

export function PartnerInvoicesPage() {
  const navigate = useNavigate()
  const [customerType, setCustomerType] = useState<CustomerFilter>('')
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)
  const [rows, setRows] = useState<Revenue[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(false)

  const apiFilters = useMemo(() => {
    const q: Record<string, unknown> = {}
    if (filters.name) q.name = filters.name
    if (filters.dueFrom) q['due_date[gte]'] = filters.dueFrom
    if (filters.dueTo) q['due_date[lte]'] = filters.dueTo
    if (filters.paidFrom) q['paid_date[gte]'] = filters.paidFrom
    if (filters.paidTo) q['paid_date[lte]'] = filters.paidTo
    if (filters.method) q.method = filters.method
    if (filters.amountFrom != null) q['amount[gte]'] = filters.amountFrom
    if (filters.amountTo != null) q['amount[lte]'] = filters.amountTo
    if (filters.status) q.status = filters.status
    return q
  }, [filters])

  const activeCount = useMemo(
    () => Object.values(apiFilters).filter((v) => v !== '' && v != null).length,
    [apiFilters],
  )

  useEffect(() => {
    let alive = true
    setLoading(true)
    PartnerService.invoices({ page, size, ...(customerType ? { customer_type: customerType } : {}), ...apiFilters })
      .then((res) => { if (!alive) return; setRows(res.data); setMeta(res.meta) })
      .catch(() => alive && toast.error('Erro ao carregar as faturas.'))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [page, size, customerType, apiFilters])
  useEffect(() => { setPage(1) }, [customerType, apiFilters, size])

  const columns: DataTableColumn<Revenue>[] = [
    { key: 'who', header: 'Cliente', alwaysVisible: true, render: (r) => (
      <div className="flex flex-col">
        <span className="font-medium text-gray-800">{r.subscriber?.name ?? r.company?.name ?? '—'}</span>
        <span className="text-[11px] text-gray-400">#{r.code}</span>
      </div>
    ) },
    { key: 'type', header: 'Tipo', align: 'center', render: (r) => r.subscriberId ? 'Associado' : r.companyId ? 'Empresa' : '—' },
    { key: 'dueDate', header: 'Vencimento', align: 'center', render: (r) => r.formatted?.dueDate ?? r.dueDate ?? '—' },
    { key: 'paidDate', header: 'Pago em', align: 'center', render: (r) => r.formatted?.paidDate ?? '—' },
    { key: 'amount', header: 'Valor', align: 'right', render: (r) => <span>{r.formatted?.amount ?? brl(r.amount)}</span> },
    { key: 'method', header: 'Método', align: 'center', render: (r) => r.method ?? '—' },
    { key: 'status', header: 'Status', align: 'center', render: (r) => <RevenueStatusDot status={r.status} /> },
  ]

  const nameLabel = customerType === 'company' ? 'Nome da empresa'
    : customerType === 'subscriber' ? 'Nome do associado' : 'Nome do cliente'

  return (
    <>
      <PageHeader title="Faturas" subtitle="Faturas dos associados e empresas da sua rede"
        crumbs={[{ label: 'Financeiro' }, { label: 'Faturas' }]} />

      {/* GroupedButtons — Todos | Empresas | Associados */}
      <div className="mb-4 grid w-full max-w-md grid-cols-3 overflow-hidden rounded-md ring-1 ring-gray-200">
        {CUSTOMER_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            onClick={() => setCustomerType(f.value)}
            className={`cursor-pointer px-3 py-1.5 text-center text-sm transition ${
              customerType === f.value ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Toolbar onFiltersClick={() => setDrawerOpen(true)} activeFiltersCount={activeCount} />

      <DataTable
        tableId="partner.invoices"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={(r) => r.id}
        emptyMessage="Nenhuma fatura encontrada."
        rowActions={(r) => [
          { key: 'view', label: 'Ver detalhes', icon: <Eye size={14} />, onClick: () => navigate(`/partner/invoices/${r.id}`) },
          ...(r.bankSlipUrl ? [{ key: 'boleto', label: 'Ver boleto', icon: <Receipt size={14} />, onClick: () => window.open(r.bankSlipUrl!, '_blank', 'noopener,noreferrer') }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <InvoiceFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        values={filters}
        nameLabel={nameLabel}
        onApply={(v) => { setFilters(v); setDrawerOpen(false) }}
        onClear={() => { setFilters(EMPTY_FILTERS); setDrawerOpen(false) }}
      />
    </>
  )
}

interface FilterDrawerProps {
  open: boolean
  onClose: () => void
  values: Filters
  nameLabel: string
  onApply: (v: Filters) => void
  onClear: () => void
}

function InvoiceFilterDrawer({ open, onClose, values, nameLabel, onApply, onClear }: FilterDrawerProps) {
  const [local, setLocal] = useState<Filters>(values)
  useEffect(() => { setLocal(values) }, [values, open])
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setLocal((p) => ({ ...p, [k]: v }))

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Filtros"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClear}>Limpar</Button>
          <Button onClick={() => onApply(local)}>Aplicar</Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input label={nameLabel} placeholder="Buscar por nome" value={local.name} onChange={(e) => set('name', e.target.value)} />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Vencimento</label>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={local.dueFrom} onChange={(e) => set('dueFrom', e.target.value)} aria-label="Vencimento de" />
            <Input type="date" value={local.dueTo} onChange={(e) => set('dueTo', e.target.value)} aria-label="Vencimento até" />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Data de pagamento</label>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={local.paidFrom} onChange={(e) => set('paidFrom', e.target.value)} aria-label="Pagamento de" />
            <Input type="date" value={local.paidTo} onChange={(e) => set('paidTo', e.target.value)} aria-label="Pagamento até" />
          </div>
        </div>

        <Select label="Forma de pagamento" value={local.method} onChange={(e) => set('method', e.target.value)} options={PAYMENT_METHODS} placeholder="Todas" />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Valor</label>
          <div className="grid grid-cols-2 gap-2">
            <CurrencyInput value={local.amountFrom} onChange={(v) => set('amountFrom', v)} placeholder="De" />
            <CurrencyInput value={local.amountTo} onChange={(v) => set('amountTo', v)} placeholder="Até" />
          </div>
        </div>

        <Select label="Status" value={local.status} onChange={(e) => set('status', e.target.value)} options={STATUS_OPTIONS} placeholder="Todos" />
      </div>
    </Sheet>
  )
}

export default PartnerInvoicesPage
